// src/pages/supplyList/SupplyApiService.js
import axios from 'axios';

/**
 * 🚀 供給履歴API専用サービス
 * APIコールを一元管理して、再利用性と保守性を向上
 */
class SupplyApiService {
  constructor() {
    this.baseURL = '/api/supplies';
    this.timeout = 10000; // 10秒タイムアウト
  }

  /**
   * 認証ヘッダーを取得
   * @returns {Object} Authorization header
   */
  getAuthHeaders() {
    const token = sessionStorage.getItem("token");
    if (!token) {
      throw new Error("認証トークンがありません");
    }
    return { Authorization: `Bearer ${token}` };
  }

  /**
   * 🚀 供給履歴を取得（フィルタリング対応）
   * @param {Object} filters - フィルター条件
   * @param {number} filters.clientId - 顧客ID
   * @param {number} filters.productId - 製品ID
   * @param {string} filters.startDate - 開始日 (YYYY-MM-DD)
   * @param {string} filters.endDate - 終了日 (YYYY-MM-DD)
   * @returns {Promise<Array>} 供給履歴データ
   */
  async getSupplyHistory(filters = {}) {
    try {
      const params = new URLSearchParams();
      
      // フィルター条件をパラメータに追加
      if (filters.clientId) params.append('clientId', filters.clientId);
      if (filters.productId) params.append('productId', filters.productId);
      if (filters.startDate) params.append('startDate', filters.startDate);
      if (filters.endDate) params.append('endDate', filters.endDate);

      const response = await axios.get(
        `${this.baseURL}/user/history?${params.toString()}`,
        {
          headers: this.getAuthHeaders(),
          timeout: this.timeout
        }
      );

      // データ検証
      const data = Array.isArray(response.data) ? response.data : [];
      
      // データ整合性チェック
      return data.filter(supply => 
        supply.id != null && 
        supply.clientName != null && 
        supply.amount != null &&
        supply.supplyDate != null
      );

    } catch (error) {

      this.handleApiError(error);
      return [];
    }
  }

  /**
   * 供給履歴を更新
   * @param {number} supplyId - 供給ID
   * @param {Object} updateData - 更新データ
   * @returns {Promise<void>}
   */
  async updateSupply(supplyId, updateData) {
    try {
      await axios.put(
        `${this.baseURL}/${supplyId}`,
        updateData,
        {
          headers: this.getAuthHeaders(),
          timeout: 5000
        }
      );
    } catch (error) {

      this.handleApiError(error);
      throw error;
    }
  }

  /**
   * 供給履歴を削除
   * @param {number} supplyId - 供給ID
   * @returns {Promise<void>}
   */
  async deleteSupply(supplyId) {
    try {
      await axios.delete(
        `${this.baseURL}/${supplyId}`,
        {
          headers: this.getAuthHeaders(),
          timeout: 5000
        }
      );
    } catch (error) {

      this.handleApiError(error);
      throw error;
    }
  }

  /**
   * 顧客一覧を取得
   * @returns {Promise<Array>} 顧客データ
   */
  async getClients() {
    try {
      const response = await axios.get('/api/clients', {
        headers: this.getAuthHeaders(),
        timeout: this.timeout
      });
      return Array.isArray(response.data) ? response.data : [];
    } catch (error) {


      return [];
    }
  }

  /**
   * 製品一覧を取得
   * @returns {Promise<Array>} 製品データ
   */
  async getProducts() {
    try {
      const response = await axios.get('/api/products', {
        headers: this.getAuthHeaders(),
        timeout: this.timeout
      });
      return Array.isArray(response.data) ? response.data : [];
    } catch (error) {

      return [];
    }
  }

  /**
   * すべての初期データを並列取得
   * @param {Object} filters - フィルター条件
   * @returns {Promise<Object>} 初期データセット
   */
  async fetchInitialData(filters = {}) {
    try {
      const [supplies, clients, products] = await Promise.all([
        this.getSupplyHistory(filters),
        this.getClients(),
        this.getProducts()
      ]);

      return {
        supplies,
        clients,
        products,
        success: true
      };
    } catch (error) {

      return {
        supplies: [],
        clients: [],
        products: [],
        success: false,
        error: error.message
      };
    }
  }

  /**
   * エラーハンドリング
   * @param {Error} error - エラーオブジェクト
   */
  handleApiError(error) {
    if (error.response) {
      // HTTPエラーレスポンス
      const status = error.response.status;
      const message = error.response.data?.message || 'サーバーエラーが発生しました';
      
      switch (status) {
        case 401:
          throw new Error("認証が切れました。再ログインしてください。");
        case 403:
          throw new Error("アクセス権限がありません。");
        case 404:
          throw new Error("データが見つかりませんでした。");
        case 500:
          throw new Error("サーバー内部エラーが発生しました。");
        default:
          throw new Error(`エラーが発生しました: ${message}`);
      }
    } else if (error.request) {
      // ネットワークエラー
      throw new Error("ネットワークエラーが発生しました。接続を確認してください。");
    } else {
      // その他のエラー
      throw new Error(error.message || "予期しないエラーが発生しました。");
    }
  }
}

// シングルトンインスタンスをエクスポート
export default new SupplyApiService();